import { BaseAdapter } from "./base-adapter";
import {
  type TEvent,
  EventType,
  type IPageViewEvent,
  type IAddToCartEvent,
  type ICheckoutStartedEvent,
  type ICheckoutCompletedEvent,
  type IProductViewEvent,
  type IViewContentEvent,
  type IPurchaseEvent,
  type ISearchEvent,
  type IAddPaymentInfoEvent,
} from "../types";

declare global {
  interface Window {
    fbq?: (
      type: string,
      eventName: string,
      params?: Record<string, any>
    ) => void;
  }
}

/**
 * Configuration for the Facebook Pixel adapter
 */
export interface PixelAdapterConfig {
  /**
   * Facebook Pixel ID
   */
  pixelId: string;
}

/**
 * Facebook Pixel tracking adapter
 * Assumes that the Facebook Pixel script is already loaded in the page
 */
export class PixelAdapter extends BaseAdapter {
  public readonly name = "FacebookPixel";

  constructor(config: PixelAdapterConfig) {
    super(config);
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize the Facebook Pixel
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      this.logger.debug("Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      this.logger.debug("Skipping initialization on server");
      return;
    }

    // Check if fbq is available
    if (!window.fbq) {
      this.logger.warn(
        "Facebook Pixel fbq not found. Make sure the script is loaded."
      );
      return;
    }

    const pixelId = this.getConfig<string>("pixelId");
    this.logger.info("Successfully initialized", { pixelId: "***" });
    this.initialized = true;
  }

  /**
   * Track an event with Facebook Pixel
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !window.fbq) {
      this.logger.warn(
        "Cannot track event, adapter not initialized or fbq not available",
        {
          eventType: event.type,
          initialized: this.initialized,
          hasWindow: typeof window !== "undefined",
          hasFbq: !!window?.fbq,
        }
      );
      return;
    }

    this.logger.debug("Tracking event", { eventType: event.type });

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event);
        break;
      case EventType.VIEW_CONTENT:
        this.trackViewContent(event);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event);
        break;
      case EventType.PURCHASE:
        this.trackPurchase(event);
        break;
      case EventType.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(event);
        break;
      case EventType.SEARCH:
        this.trackSearch(event);
        break;
      default:
        this.logger.debug("Unhandled event type", { eventType: event.type });
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    const params = this.enhanceWithAffiliateParams({});
    window.fbq?.("track", "PageView", params);
    this.logger.debug("PageView event tracked", { params });
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    const params = {
      content_type: "product_group",
      content_ids: [event.productId],
      content_name: event.productName,
      content_category: event.category,
      value: event.price,
      currency: event.currency || "INR",
    };

    window.fbq?.(
      "track",
      "ViewContent",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    const params = {
      content_type: "product_group",
      content_ids: [event.productId],
      content_name: event.productName,
      value: event.price,
      currency: event.currency || "INR",
    };

    const enhancedParams = this.enhanceWithAffiliateParams(params);
    window.fbq?.("track", "AddToCart", enhancedParams);
    this.logger.debug("AddToCart event tracked", { params: enhancedParams });
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    const params = {
      content_ids: event.items.map((item) => item.productId),
      content_type: "product_group",
      value: event.cartValue,
      currency: event.currency || "INR",
      num_items: event.itemCount,
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
    };

    window.fbq?.(
      "track",
      "InitiateCheckout",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    const params = {
      value: event.cartValue,
      currency: event.currency || "INR",
      content_type: "product_group",
      content_ids: event.items.map((item) => item.productId),
      num_items: event.itemCount,
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
    };

    window.fbq?.("track", "Purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    const params = {
      search_string: event.searchTerm,
      content_category: "product",
      content_ids: event.content_ids || [],
    };

    window.fbq?.("track", "Search", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add payment info event
   * @param event The add payment info event
   */
  private trackAddPaymentInfo(event: IAddPaymentInfoEvent): void {
    const params = {
      value: event.cartValue,
      currency: event.currency || "INR",
      content_type: "product_group",
      content_ids: event.items.map((item) => item.productId),
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
      payment_type: event.paymentType || "",
    };

    window.fbq?.(
      "track",
      "AddPaymentInfo",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a view content event
   * @param event The view content event
   */
  private trackViewContent(event: IViewContentEvent): void {
    const params = {
      content_type: event.content_type || "product",
      content_ids: event.content_ids || [],
      content_name: event.content_name || "",
      content_category: event.content_category || "",
      value: event.value || 0,
      currency: event.currency || "INR",
    };

    window.fbq?.(
      "track",
      "ViewContent",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a purchase event
   * @param event The purchase event
   */
  private trackPurchase(event: IPurchaseEvent): void {
    const params = {
      value: event.value,
      currency: event.currency || "INR",
      content_type: event.content_type || "product_group",
      content_ids: event.content_ids || [],
      num_items: event.num_items || 1,
      contents: event.contents || [],
    };

    window.fbq?.("track", "Purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Convert an event to Facebook Pixel parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return this.enhanceWithAffiliateParams(params);
  }
}
